import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { BUILD_CODE, DISPLAY_VERSION } from '../buildInfo';

export const APP_VERSION_DISMISSED_SEQUENCE_KEY = '9jawin_app_version_dismissed_sequence';
export const APP_VERSION_UPDATE_TARGET_KEY = '9jawin_app_version_update_target_build';
export const APP_VERSION_CACHE_BUST_PARAM = '__9jawin_update';
const VERSION_RE = /^\d+\.\d+\.\d+\.\d+$/;

export function parsePublishedVersion(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const displayVersion = typeof payload.displayVersion === 'string' ? payload.displayVersion.trim() : '';
  const latestBuildCode = Number(payload.latestBuildCode);
  const minimumBuildCode = Number(payload.minimumBuildCode);
  const sequence = Number(payload.sequence);
  if (!VERSION_RE.test(displayVersion)
    || !Number.isSafeInteger(latestBuildCode) || latestBuildCode < 1
    || !Number.isSafeInteger(minimumBuildCode) || minimumBuildCode < 0
    || minimumBuildCode > latestBuildCode
    || !Number.isSafeInteger(sequence) || sequence < 1) return null;
  return {
    displayVersion,
    latestBuildCode,
    minimumBuildCode,
    sequence,
    note: typeof payload.note === 'string' ? payload.note.slice(0,200) : '',
    publishedAt: typeof payload.publishedAt === 'string' ? payload.publishedAt : '',
    updateUrl: typeof payload.updateUrl === 'string' && payload.updateUrl.trim() ? payload.updateUrl.trim() : null,
  };
}

function scopedKey(base, userKey) { return base + ':' + String(userKey || 'authenticated'); }
function readDismissedSequence(userKey) {
  try {
    const value=Number(window.sessionStorage.getItem(scopedKey(APP_VERSION_DISMISSED_SEQUENCE_KEY,userKey)));
    return Number.isSafeInteger(value) && value >= 1 ? value : null;
  } catch (_) { return null; }
}
function writeDismissedSequence(sequence, userKey) {
  try { window.sessionStorage.setItem(scopedKey(APP_VERSION_DISMISSED_SEQUENCE_KEY,userKey),String(sequence)); } catch (_) {}
}
function readUpdateTarget(userKey) {
  try {
    const value=Number(window.sessionStorage.getItem(scopedKey(APP_VERSION_UPDATE_TARGET_KEY,userKey)));
    return Number.isSafeInteger(value) && value >= 1 ? value : null;
  } catch (_) { return null; }
}
function writeUpdateTarget(buildCode, userKey) {
  try { window.sessionStorage.setItem(scopedKey(APP_VERSION_UPDATE_TARGET_KEY,userKey),String(buildCode)); } catch (_) {}
}
function clearUpdateTarget(userKey) {
  try { window.sessionStorage.removeItem(scopedKey(APP_VERSION_UPDATE_TARGET_KEY,userKey)); } catch (_) {}
}

export function cacheBustReload(targetBuildCode) {
  const url=new URL(window.location.href);
  url.searchParams.set(APP_VERSION_CACHE_BUST_PARAM,String(targetBuildCode));
  window.location.replace(url.toString());
}

export async function clearAppCaches() {
  try {
    if ('serviceWorker' in navigator) {
      const registrations=await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r=>r.unregister().catch(()=>false)));
    }
  } catch (_) {}
  try {
    if ('caches' in window) {
      const names=await caches.keys();
      await Promise.all(names.map(name=>caches.delete(name).catch(()=>false)));
    }
  } catch (_) {}
}

function isNativeShell() {
  try {
    return Boolean(window.Capacitor?.isNativePlatform?.() || window.Capacitor?.getPlatform?.() && window.Capacitor.getPlatform() !== 'web');
  } catch (_) { return false; }
}

function isGameplayPath(pathname) {
  return /^\/play\/[^/]+\//.test(pathname)
    || /^\/tournaments\/[^/]+\/play(?:\/|$)/.test(pathname)
    || /^\/match\/[^/]+(?:\/|$)/.test(pathname)
    || /^\/room-waiting\/[^/]+(?:\/|$)/.test(pathname);
}

export default function AppVersionGate({ children }) {
  const location=useLocation();
  const { user, initialized, isAuthenticated }=useAuth();
  const busy=useMemo(()=>isGameplayPath(location.pathname),[location.pathname]);
  const userKey=String(user?.id ?? user?.userId ?? 'authenticated');
  const [published,setPublished]=useState(null);
  const [prompt,setPrompt]=useState(null);
  const [updateFailed,setUpdateFailed]=useState(false);
  const [updating,setUpdating]=useState(false);
  const [checking,setChecking]=useState(false);
  const [checkedUserId,setCheckedUserId]=useState(null);
  const previousAuth=useRef(false);
  const pendingHeaderCheck=useRef(false);

  const checkVersion=useCallback(async ({blockInitial=false}={})=>{
    if (!isAuthenticated || (busy && !blockInitial)) return null;
    if (blockInitial) setChecking(true);
    try {
      const response=await fetch('/app-version.json',{cache:'no-store',credentials:'same-origin'});
      if (!response.ok) { setPublished(null); return null; }
      const next=parsePublishedVersion(await response.json());
      if (!next) { setPublished(null); return null; }
      setPublished(next);
      return next;
    } catch (_) {
      setPublished(null);
      return null;
    } finally {
      if (blockInitial) setChecking(false);
    }
  },[busy,isAuthenticated]);

  useEffect(()=>{
    if (!initialized) return;
    if (!isAuthenticated) {
      setPublished(null);
      setPrompt(null);
      setUpdateFailed(false);
      setCheckedUserId(null);
      previousAuth.current=false;
      return;
    }
    const id=String(user?.id ?? user?.userId ?? 'authenticated');
    if (checkedUserId === id) return;
    previousAuth.current=true;
    setChecking(true);
    checkVersion({blockInitial:true}).finally(()=>setCheckedUserId(id));
  },[initialized,isAuthenticated,user,checkedUserId,checkVersion]);

  useEffect(()=>{
    if (!published || !isAuthenticated) {
      setPrompt(null);
      return;
    }
    if (BUILD_CODE >= published.latestBuildCode) {
      clearUpdateTarget(userKey);
      setPrompt(null);
      setUpdateFailed(false);
      return;
    }
    const target=readUpdateTarget(userKey);
    if (target === published.latestBuildCode) {
      setPrompt(null);
      setUpdateFailed(true);
      return;
    }
    const dismissed=readDismissedSequence(userKey);
    if (published.latestBuildCode > BUILD_CODE && published.sequence === dismissed && published.minimumBuildCode <= BUILD_CODE) {
      setPrompt(null);
      setUpdateFailed(false);
      return;
    }
    setUpdateFailed(false);
    setPrompt(published);
  },[published,isAuthenticated]);

  useEffect(()=>{
    const onApiVersionHint=(event)=>{
      const latest=Number(event.detail?.latestBuildCode);
      const minimum=Number(event.detail?.minimumBuildCode);
      if (!isAuthenticated || ![latest,minimum].some(Number.isSafeInteger) || ![latest,minimum].some(value=>value > BUILD_CODE)) return;
      pendingHeaderCheck.current=true;
      if (!busy) {
        pendingHeaderCheck.current=false;
        checkVersion();
      }
    };
    window.addEventListener('9jawin-app-version-hint', onApiVersionHint);
    return ()=>window.removeEventListener('9jawin-app-version-hint', onApiVersionHint);
  },[busy,isAuthenticated,checkVersion]);

  useEffect(()=>{
    if (!busy && pendingHeaderCheck.current && isAuthenticated) {
      pendingHeaderCheck.current=false;
      checkVersion();
    }
  },[busy,isAuthenticated,checkVersion]);

  useEffect(()=>{
    const onVisibility=()=>{
      if (document.visibilityState === 'visible' && isAuthenticated && !busy) checkVersion();
    };
    document.addEventListener('visibilitychange',onVisibility);
    return ()=>document.removeEventListener('visibilitychange',onVisibility);
  },[busy,isAuthenticated,checkVersion]);

  const apply=async()=>{
    if (!prompt || updating) return;
    setUpdating(true);
    setUpdateFailed(false);
    writeUpdateTarget(prompt.latestBuildCode,userKey);
    if (isNativeShell() && prompt.updateUrl) {
      window.location.assign(prompt.updateUrl);
      return;
    }
    await clearAppCaches();
    cacheBustReload(prompt.latestBuildCode);
  };

  const cancel=()=>{
    if (!prompt || prompt.minimumBuildCode > BUILD_CODE) return;
    writeDismissedSequence(prompt.sequence,userKey);
    setPrompt(null);
  };

  const retryUpdate=()=>{
    if (!published || BUILD_CODE >= published.latestBuildCode) {
      setUpdateFailed(false);
      clearUpdateTarget(userKey);
      return;
    }
    setUpdateFailed(false);
    setUpdating(false);
    setPrompt(published);
  };

  const initialAuthCheckPending=isAuthenticated && checkedUserId !== String(user?.id ?? user?.userId ?? 'authenticated');
  if (!initialized || (initialAuthCheckPending && checking)) {
    return <div style={{position:'fixed',inset:0,zIndex:2147483647,background:'#0a0f0e',display:'grid',placeItems:'center',color:'#aab8b3',fontSize:13}}>Loading…</div>;
  }

  if (!isAuthenticated) return children;

  const requiredBlock=Boolean(prompt && prompt.minimumBuildCode > BUILD_CODE);
  const overlay={position:'fixed',inset:0,zIndex:2147483647,background:'rgba(0,0,0,.94)',display:'grid',placeItems:'center',padding:16,boxSizing:'border-box'};
  const modal={width:'100%',maxWidth:430,background:'#101615',border:'1px solid #2b403a',borderRadius:18,padding:20,boxSizing:'border-box',color:'#fff',boxShadow:'0 18px 60px rgba(0,0,0,.7)'};
  const updateButton={width:'100%',padding:14,border:0,borderRadius:11,background:'#00d4aa',color:'#06110e',fontWeight:900,fontSize:15};
  const cancelButton={...updateButton,marginTop:8,background:'#202b28',color:'#d7e1dd'};

  if (checking && !initialAuthCheckPending && !busy) {
    return <div style={{position:'fixed',inset:0,zIndex:2147483647,background:'#0a0f0e',display:'grid',placeItems:'center',color:'#aab8b3',fontSize:13}}>Checking for updates…</div>;
  }

  if (updateFailed) return <div role="alertdialog" aria-modal="true" style={overlay}><div style={modal}>
    <div style={{fontSize:11,letterSpacing:'.12em',fontWeight:900,color:'#ffbd66'}}>UPDATE FAILED</div>
    <h2 style={{margin:'8px 0 6px',fontSize:20}}>Update could not finish</h2>
    <p style={{margin:'0 0 18px',color:'#aab8b3',fontSize:13,lineHeight:1.5}}>Update could not finish. Close the app and reopen it, or check your connection.</p>
    <button type="button" autoFocus onClick={retryUpdate} style={updateButton}>Try again</button>
  </div></div>;

  if (requiredBlock) return <div role="alertdialog" aria-modal="true" aria-labelledby="app-version-title" style={overlay} data-build-code={BUILD_CODE}>
    <div style={modal}>
      <div style={{fontSize:11,letterSpacing:'.12em',fontWeight:900,color:'#00d4aa'}}>REQUIRED UPDATE</div>
      <h2 id="app-version-title" style={{margin:'8px 0 6px',fontSize:22}}>9jaWin {prompt.displayVersion}</h2>
      {prompt.note && <p style={{margin:'0 0 18px',color:'#aab8b3',fontSize:13,lineHeight:1.5,whiteSpace:'pre-wrap'}}>{prompt.note}</p>}
      <button type="button" autoFocus onClick={apply} disabled={updating} style={{...updateButton,opacity:updating?.65:1}}>{updating?'Updating…':'Update Now'}</button>
    </div>
  </div>;

  if (prompt) return <div role="dialog" aria-modal="true" aria-labelledby="app-version-title" style={overlay} data-build-code={BUILD_CODE}>
    <div style={modal}>
      <div style={{fontSize:11,letterSpacing:'.12em',fontWeight:900,color:'#00d4aa'}}>NEW VERSION AVAILABLE</div>
      <h2 id="app-version-title" style={{margin:'8px 0 6px',fontSize:22}}>9jaWin {prompt.displayVersion}</h2>
      {prompt.note && <p style={{margin:'0 0 18px',color:'#aab8b3',fontSize:13,lineHeight:1.5,whiteSpace:'pre-wrap'}}>{prompt.note}</p>}
      <button type="button" autoFocus onClick={apply} disabled={updating} style={{...updateButton,opacity:updating?.65:1}}>{updating?'Updating…':'Update Now'}</button>
      <button type="button" onClick={cancel} disabled={updating} style={{...cancelButton,opacity:updating?.65:1}}>Cancel</button>
    </div>
  </div>;

  return children;
}
